/**
 * Signal Feedback Modal
 *
 * Modal for submitting signal corrections/feedback.
 * Allows users to correct misinterpreted signals.
 */

import { useState } from 'react'
import { motion } from 'framer-motion'
import { useSubmitFeedback } from '@/hooks/usePragmatic'
import type { SignalType } from '@personic/shared'

interface SignalFeedbackModalProps {
  profileId: string
  signalType?: SignalType
  originalInterpretation?: string
  originalValue?: number
  onClose: () => void
}

const signalTypeOptions: Array<{ value: SignalType; label: string }> = [
  { value: 'certainty', label: 'Certainty' },
  { value: 'hedging', label: 'Hedging' },
  { value: 'commitment', label: 'Commitment' },
]

export default function SignalFeedbackModal({
  profileId,
  signalType: initialSignalType,
  originalInterpretation: initialInterpretation,
  originalValue: initialValue,
  onClose,
}: SignalFeedbackModalProps) {
  const [signalType, setSignalType] = useState<SignalType>(
    initialSignalType || 'certainty'
  )
  const [originalInterpretation, setOriginalInterpretation] = useState(
    initialInterpretation || ''
  )
  const [userCorrection, setUserCorrection] = useState('')
  const [correctedValue, setCorrectedValue] = useState<string>('')
  const [showSuccess, setShowSuccess] = useState(false)

  const submitFeedback = useSubmitFeedback(profileId)

  const handleSubmit = async () => {
    try {
      await submitFeedback.mutateAsync({
        signal_type: signalType,
        original_interpretation: originalInterpretation,
        user_correction: userCorrection,
        original_value: initialValue,
        corrected_value: correctedValue ? parseFloat(correctedValue) : undefined,
      })
      setShowSuccess(true)
      setTimeout(() => onClose(), 1500)
    } catch {
      // Error handled by React Query
    }
  }

  const isValid = originalInterpretation.trim() && userCorrection.trim()

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6"
      >
        {showSuccess ? (
          <div className="text-center py-8">
            <div className="text-5xl mb-4">✓</div>
            <h3 className="text-lg font-semibold text-gray-900">
              Feedback Submitted
            </h3>
            {submitFeedback.data?.baseline_adjusted && (
              <span className="inline-flex items-center mt-3 px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full">
                Baseline adjusted
              </span>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                Signal Correction
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Signal Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Signal Type
                </label>
                <select
                  value={signalType}
                  onChange={(e) => setSignalType(e.target.value as SignalType)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-engage-primary/20 focus:border-engage-primary"
                >
                  {signalTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Original Interpretation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  System Interpretation
                </label>
                <input
                  type="text"
                  value={originalInterpretation}
                  onChange={(e) => setOriginalInterpretation(e.target.value)}
                  readOnly={!!initialInterpretation}
                  className={`w-full px-4 py-2 border border-gray-200 rounded-lg text-sm ${
                    initialInterpretation ? 'bg-gray-50 text-gray-600' : ''
                  }`}
                  placeholder="What the system detected..."
                />
              </div>

              {/* User Correction */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Correction
                </label>
                <textarea
                  value={userCorrection}
                  onChange={(e) => setUserCorrection(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-engage-primary/20 focus:border-engage-primary resize-none"
                  placeholder="What you believe the correct interpretation should be..."
                />
              </div>

              {/* Corrected Value (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Corrected Value{' '}
                  <span className="text-gray-400 font-normal">(optional, 0-1)</span>
                </label>
                <input
                  type="number"
                  value={correctedValue}
                  onChange={(e) => setCorrectedValue(e.target.value)}
                  min="0"
                  max="1"
                  step="0.05"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-engage-primary/20 focus:border-engage-primary"
                  placeholder="0.0 - 1.0"
                />
              </div>
            </div>

            {/* Error */}
            {submitFeedback.isError && (
              <div className="mt-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">
                Failed to submit feedback. Please try again.
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isValid || submitFeedback.isPending}
                className="px-4 py-2 bg-engage-primary text-white rounded-lg text-sm font-medium hover:bg-engage-primary/90 transition-colors disabled:opacity-50"
              >
                {submitFeedback.isPending ? 'Submitting...' : 'Submit Correction'}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  )
}
